import { useFindMany } from "@gadgetinc/react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/16/solid";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../api";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { SegmentFiltersTooltip } from "../../../externalComponents/SegmentFiltersTooltip";
import { Badge } from "../../../externalComponents/tailwind/badge";
import { Button } from "../../../externalComponents/tailwind/button";
import EmptyState from "../../../externalComponents/tailwind/empty-state";
import { Link } from "../../../externalComponents/tailwind/link";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../externalComponents/tailwind/table";
import { Text } from "../../../externalComponents/tailwind/text";

export default function PastSendings({ user, isMobile }) {
  const navigate = useNavigate();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const NUM_ON_PAGE = 5;
  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });
  const [pageNumber, setPageNumber] = useState(1);

  const [{ data, fetching }] = useFindMany(api.sending, {
    ...cursor,
    filter: {
      customerId: { equals: user.customerId },
      status: { equals: "completed" },
      isTest: { notEquals: true },
      campaign: {
        automationType: { isSet: false },
      },
    },
    select: {
      id: true,
      contactList: true,
      sendingDate: true,
      contactListSegment: {
        externalId: true,
        type: true,
      },
      segment: {
        id: true,
        name: true,
      },
      segmentFilters: true,
      campaign: {
        name: true,
        trigger: true,
        id: true,
      },
    },
    sort: {
      sendingDate: "Descending",
    },
  });

  const getNextPage = () => {
    if (data?.endCursor) {
      setCursor({ first: NUM_ON_PAGE, after: data.endCursor });
      setPageNumber(pageNumber + 1);
    }
  };

  const getPreviousPage = () => {
    if (pageNumber > 1 && data?.startCursor) {
      setCursor({ last: NUM_ON_PAGE, before: data.startCursor });
      setPageNumber(pageNumber - 1);
    }
  };

  const rowMarkup = data?.map(
    (
      {
        id,
        contactList,
        sendingDate,
        campaign,
        contactListSegment,
        segment,
        segmentFilters,
      },
      index,
    ) => (
      <TableRow
        id={id}
        key={`past-${id}`}
        position={index}
        {...(!isMobile && {
          onClick: () => navigate(`/campaigns/edit/${campaign.id}`),
        })}
      >
        <TableCell>{campaign?.name}</TableCell>
        <TableCell>
          {sendingDate.toLocaleDateString("fr-FR", {
            weekday: "long",
            month: "long",
            year: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          }) + ` [${timezone}]`}
        </TableCell>
        <TableCell className="text-center">{contactList.length}</TableCell>
        <TableCell className="w-[30%]">
          {segment ? (
            <SegmentFiltersTooltip segmentFilters={segmentFilters}>
              <span className="cursor-help">{segment.name}</span>
            </SegmentFiltersTooltip>
          ) : contactListSegment?.type === "klaviyo-segment" ? (
            <Link
              style={{
                wordBreak: "break-all",
                overflowWrap: "break-word",
              }}
              className="hover:underline"
              href={`https://www.klaviyo.com/list/${contactListSegment?.externalId}`}
              target="_blank"
            >
              {`https://www.klaviyo.com/list/${contactListSegment?.externalId}`}
            </Link>
          ) : (
            "-"
          )}
        </TableCell>
      </TableRow>
    ),
  );

  if (!data && fetching) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <br />
      <Text className="text-xl font-bold">Past messages</Text>
      <br />
      {data?.length > 0 ? (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Campaign name</TableHeader>
                <TableHeader>Sending date</TableHeader>
                <TableHeader className="text-center">Recipients</TableHeader>
                <TableHeader className="w-[30%]">Segment</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>{rowMarkup}</TableBody>
          </Table>
          <div className="flex justify-start my-4">
            <Button
              color="secondaryButton"
              onClick={getPreviousPage}
              disabled={!data?.hasPreviousPage}
            >
              <ArrowLeftIcon />
              Previous
            </Button>
            <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
              {pageNumber}
            </Badge>
            <Button
              color="secondaryButton"
              onClick={getNextPage}
              disabled={!data?.hasNextPage}
            >
              Next
              <ArrowRightIcon />
            </Button>
          </div>
        </>
      ) : (
        <EmptyState heading="No message has been sent" />
      )}
    </>
  );
}
